import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageStatus, SkeletonLoader, ToggleBox } from "@unified-checkout/design-system";
import { CircleExclamation } from "@unified-checkout/design-system/icons/index";
import { MOBILE_BREAKPOINT } from "@app/dashboard/constants/break-points";
import { LOCAL_STORAGE_KEYS } from "@app/dashboard/constants/local-storage-keys";
import { PROFILE } from "@app/dashboard/constants/profile";
import { useScreenWidth } from "@app/dashboard/hooks/useScreenWidth";
import { useServicesStore } from "@app/dashboard/store/services";
import { getProfileType } from "@app/dashboard/utils/localStorageUtils";
import { getFromLocalStorage } from "@app/dashboard/utils/manageLocalStorage";
import { Card } from "@app/dashboard/components/inner_layout/Cards/CardWrapper";
import { Header } from "./Header";
import { DigestModal } from "./Modals/Digest";
import { InstantSetModal } from "./Modals/InstantSetComponent";
import { MerchantStatementModal } from "./Modals/MerchantStatement";
import styles from "./services.module.scss";

export const Services = () => {
    const { t } = useTranslation();
    const screenWidth = useScreenWidth();
    const isMobile = screenWidth <= MOBILE_BREAKPOINT;

    const digestData = getFromLocalStorage(LOCAL_STORAGE_KEYS.DIGEST);
    const merchantStatementsData = getFromLocalStorage(LOCAL_STORAGE_KEYS.MERCHANT_STATEMENTS);

    const hasMerchantStatements = merchantStatementsData?.enabled != null;
    const hasDigest = digestData?.enabled != null;
    const isMerchantType = getProfileType() === PROFILE.TYPE.MERCHANT;

    const isDigestEnabledInStore = useServicesStore((state) => state.isDigestEnabled);
    const isMerchantStatementEnabledInStore = useServicesStore((state) => state.isMerchantStatementEnabled);
    const isLoading = useServicesStore((state) => state.isLoading);

    const [digestState, setDigestState] = useState({
        isOpen: false,
        isEnabled: digestData?.enabled ?? false
    });
    const [merchantStatementState, setMerchantStatementState] = useState({
        isOpen: false,
        isEnabled:merchantStatementsData?.enabled ?? false
    });
    const [instantSetState, setInstantSetState] = useState({
        isOpen: false,
        isEnabled: false
    });

    useEffect(() => {
        if (isDigestEnabledInStore !== undefined ) {
            setDigestState((prev) => ({ ...prev, isEnabled: digestData?.enabled }));
        }
        if (isMerchantStatementEnabledInStore !== undefined ) {
            setMerchantStatementState((prev) => ({ ...prev, isEnabled: merchantStatementsData?.enabled }));
        }
    }, [isMerchantStatementEnabledInStore ,isDigestEnabledInStore ]);

    const handleDigestToggleClick = async () => {
        setDigestState((prev) => ({ ...prev, isOpen: true }));
        return false;
    };

    const handleMerchantStatementToggleClick = async () => {
        setMerchantStatementState((prev) => ({ ...prev, isOpen: true }));
        return false;
    };

    const handleInstantSetToggleClick = async () => {
        setInstantSetState((prev) => ({ ...prev, isOpen: true }));
        return false;
    };

    const { isEnabled: isDigestEnabled } = digestState;
    const { isEnabled: isMerchantStatementEnabled } = merchantStatementState;
    const { isEnabled: isInstantSetEnabled } = instantSetState;

    const toggleBoxs = [
        {
            isVisible: hasMerchantStatements,
            text: t('innerLayout.servicesPage.toggleBoxs.merchantStatement'),
            hintText: t('innerLayout.servicesPage.toggleBoxs.merchantStatementHintText'),
            onBeforeToggleConfirmation: handleMerchantStatementToggleClick,
            checked: isMerchantStatementEnabled,
            disabled: isLoading
        },
        {
            isVisible: isMerchantType,
            text: t('innerLayout.servicesPage.toggleBoxs.InstantSettlement'),
            hintText: t('innerLayout.servicesPage.toggleBoxs.InstantSettlementHintText'),
            onBeforeToggleConfirmation: handleInstantSetToggleClick,
            checked: isInstantSetEnabled,
            disabled: !isInstantSetEnabled
        },
        {
            isVisible: hasDigest,
            text: t('innerLayout.servicesPage.toggleBoxs.digestSubscription'),
            hintText: t('innerLayout.servicesPage.toggleBoxs.digestSubscriptionHintText'),
            onBeforeToggleConfirmation: handleDigestToggleClick,
            checked: isDigestEnabled,
            disabled: isLoading
        }
    ];

    const visibleToggleBoxes = toggleBoxs.filter((box) => box.isVisible);
    const skeletonRowsCount = visibleToggleBoxes.length > 1 ? visibleToggleBoxes.length : 3;

    const servicesCard =
        <Card>
            {isLoading ?
                <SkeletonLoader
                    noOfRows={skeletonRowsCount}
                    height={`${skeletonRowsCount * 4}rem`}
                    type="transparent_box"
                />
                :
                visibleToggleBoxes.map((data) => (
                    <ToggleBox
                        key={data.text}
                        type="transparent box"
                        text={data.text}
                        hintText={data.hintText}
                        onBeforeToggleConfirmation={data.onBeforeToggleConfirmation}
                        checked={data.checked}
                        disabled={data.disabled}
                    />
                ))}
        </Card>

    const digestModalProps = {
        isDigestOpen: digestState.isOpen,
        setisDigestOpen: (value: boolean) => setDigestState((prev) => ({ ...prev, isOpen: value })),
        setIsDigestEnabled: (value: boolean) => setDigestState((prev) => ({ ...prev, isEnabled: value })),
        isDigestEnabled: digestState.isEnabled
    };

    const merchantStatementModalProps = {
        isMerchantStatementsOpen: merchantStatementState.isOpen,
        setisMerchantStatementsOpen: (value: boolean) => setMerchantStatementState((prev) => ({ ...prev, isOpen: value })),
        setIsMerchantStatementEnabled: (value: boolean) => setMerchantStatementState((prev) => ({ ...prev, isEnabled: value })),
        isMerchantStatementEnabled: merchantStatementState.isEnabled
    };

    const instantSetModalProps = {
        isInstantSetOpen: instantSetState.isOpen,
        setisInstantSetOpen: (value: boolean) => setInstantSetState((prev) => ({ ...prev, isOpen: value })),
        isInstantSetEnabled: instantSetState.isEnabled,
        setIsInstantSetEnabled: (value: boolean) => setInstantSetState((prev) => ({ ...prev, isEnabled: value }))
    };

    const modals =
        <>
            <DigestModal {...digestModalProps} />
            <MerchantStatementModal {...merchantStatementModalProps} />
            <InstantSetModal {...instantSetModalProps} />
        </>

    const renderContent = () => {
        const title = (
            <div className={styles["services__main__col__title"]}>
                {t('innerLayout.servicesPage.features')}
            </div>
        );

        if (isMobile) {
            return (
                <>
                    {title}
                    {servicesCard}
                </>
            );
        }

        return (
            <>
                <div className={styles["services__main__col"]}>
                    {title}
                </div>
                <div className={styles["services__main__col"]}>
                    {servicesCard}
                </div>
            </>
        );
    };

    return (
        <div className={styles["services"]}>
            {!isLoading && visibleToggleBoxes.length === 0 ?
                <PageStatus
                    status="error"
                    title={t('innerLayout.errors.smthWentWrong')}
                    description={t('innerLayout.errors.dataRetrievalErr')}
                    icon={CircleExclamation}
                />
                :
                <>
                    <Header />

                    <div className={styles["services__main"]}>
                        {renderContent()}
                    </div>

                    <div className={styles["services__modal"]}>
                        {modals}
                    </div>
                </>
            }
        </div>
    )
}